import React, { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FaArchive } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { getMyPoolsHistory } from "../api/premierLeague";
import PageHeader from "../components/PageHeader";

const TOP_BAR_BLUE = "#00005f";
const PAGE_BLUE = "#00007f";
const GOLD = "#c9a227";

function gameLabel(pool) {
  if (pool.game === "mlb_hr_derby") return "MLB Home Run Derby";
  if (pool.game === "world_cup") return "World Cup Pick 'Em";
  if (pool.game === "mlb_playoff") return "MLB Playoff Challenge";
  if (pool.game === "nfl_survivor") return "NFL Survivor";
  if (pool.game === "nfl_pickem") return "NFL Pick 'Em";
  return "Premier League Pick 'Em";
}

function poolRoute(pool) {
  if (pool.game === "mlb_hr_derby") return `/mlb-hr-derby/current/${pool.pool_id}`;
  if (pool.game === "world_cup") return `/world-cup/current/${pool.pool_id}`;
  if (pool.game === "mlb_playoff") return `/mlb-playoff/current/${pool.pool_id}`;
  if (pool.game === "nfl_survivor") return `/nfl-survivor/current/${pool.pool_id}`;
  if (pool.game === "nfl_pickem") return `/nfl-pickem/current/${pool.pool_id}`;
  return `/premier-league/current/${pool.pool_id}`;
}

function formatDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });
}

function PoolCard({ pool, archived }) {
  const navigate = useNavigate();

  return (
    <Box
      bg="white"
      color="black"
      borderRadius="lg"
      border="2px solid"
      borderColor={archived ? "gray.200" : TOP_BAR_BLUE}
      boxShadow={archived ? "sm" : "md"}
      p={5}
      opacity={archived ? 0.85 : 1}
    >
      <VStack align="stretch" spacing={3}>
        <Flex justify="space-between" align="flex-start">
          <Box>
            <Text fontWeight="bold" fontSize="lg" color={TOP_BAR_BLUE}>
              {pool.pool_name}
            </Text>
            <Text fontSize="sm" color="gray.500">
              {gameLabel(pool)}
            </Text>
          </Box>
          {archived ? (
            <Badge colorScheme="gray" fontSize="xs">Archived</Badge>
          ) : (
            <Badge colorScheme="green" fontSize="xs">Active</Badge>
          )}
        </Flex>

        <Box>
          <Text fontSize="sm" color="gray.600">
            <Text as="span" fontWeight="semibold">Season:</Text> {pool.season_name}
          </Text>
          {pool.season_end_date && (
            <Text fontSize="sm" color="gray.600">
              <Text as="span" fontWeight="semibold">
                {archived ? "Ended:" : "Ends:"}
              </Text>{" "}
              {formatDate(pool.season_end_date)}
            </Text>
          )}
          <Text fontSize="sm" color="gray.600">
            <Text as="span" fontWeight="semibold">Participants:</Text> {pool.user_count}
          </Text>
          {pool.pool_depth && (
            <Text fontSize="sm" color="gray.600">
              <Text as="span" fontWeight="semibold">Pot:</Text>{" "}
              ${pool.user_count * pool.pool_depth}
            </Text>
          )}
        </Box>

        <Button
          size="sm"
          bg={archived ? "gray.100" : TOP_BAR_BLUE}
          color={archived ? "gray.700" : "white"}
          border={archived ? "1px solid" : "none"}
          borderColor="gray.300"
          onClick={() => navigate(poolRoute(pool), { state: { seasonId: pool.season_id } })}
          _hover={{ bg: archived ? "gray.200" : "#00004a" }}
        >
          {archived ? "View Final Results" : "Open Pool"}
        </Button>
      </VStack>
    </Box>
  );
}

export default function MyPoolsPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    getMyPoolsHistory()
      .then(setPools)
      .catch(() => setError("Failed to load pools."))
      .finally(() => setLoading(false));
  }, []);

  const activePools = pools.filter((p) => !p.is_archived);
  const archivedPools = pools.filter((p) => p.is_archived);

  function handleLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  return (
    <Box minH="100vh" bg={PAGE_BLUE} color="white">
      <PageHeader
        title="MY POOLS"
        onBack={() => navigate("/")}
        user={user}
        onLogout={handleLogout}
      />

      {/* Body */}
      <Container maxW="container.xl" py={10}>
        {loading ? (
          <Flex justify="center" py={20}>
            <Spinner size="xl" />
          </Flex>
        ) : error ? (
          <Text color="red.300" fontSize="lg">{error}</Text>
        ) : pools.length === 0 ? (
          <Box
            bg="whiteAlpha.100"
            borderRadius="lg"
            p={10}
            textAlign="center"
          >
            <Text fontSize="xl" color="whiteAlpha.800">
              You haven&apos;t joined any pools yet.
            </Text>
            <Button
              mt={4}
              bg="orange.400"
              color="white"
              onClick={() => navigate("/premier-league")}
              _hover={{ bg: "orange.500" }}
            >
              Browse Pools
            </Button>
          </Box>
        ) : (
          <VStack align="stretch" spacing={10}>
            {/* Active */}
            {activePools.length > 0 && (
              <Box>
                <Heading size="md" mb={4} color="whiteAlpha.900">
                  Active
                </Heading>
                <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4}>
                  {activePools.map((p) => (
                    <PoolCard key={`${p.season_id}-${p.pool_id}`} pool={p} archived={false} />
                  ))}
                </SimpleGrid>
              </Box>
            )}

            {/* Archived */}
            {archivedPools.length > 0 && (
              <Box>
                <HStack mb={4} spacing={2} align="center">
                  <FaArchive color={GOLD} />
                  <Heading size="md" color="whiteAlpha.900">
                    Past Seasons
                  </Heading>
                </HStack>
                <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4}>
                  {archivedPools.map((p) => (
                    <PoolCard key={`${p.season_id}-${p.pool_id}`} pool={p} archived={true} />
                  ))}
                </SimpleGrid>
              </Box>
            )}
          </VStack>
        )}
      </Container>
    </Box>
  );
}
